import { graphql } from "gatsby"
import Img from "gatsby-image"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React from "react"
import { css } from "styled-components"
import {
  ContentfulResponsiveImage,
  IContentfulResponsiveImage,
  TContentfulImage,
} from "../atoms/breakpoint-image"
import { T, Text } from "../atoms/text"
import VariantContainer from "../atoms/variant-container"
import ContentBlock from "./content-block"

export interface ITestimonialCard {
  __typename: "ContentfulTestimonial"
  contentful_id: string
  logos:
    | TContentfulImage[]
    | {
        file: { url: string }
      }[]
  image?: IContentfulResponsiveImage
  quote: RenderRichTextData<ContentfulRichTextGatsbyReference> | string
  author: string
  jobTitle?: string
  organization?: string
  active?: boolean
}

export const TestimonialCard = ({
  logos,
  image,
  quote,
  author = "",
  jobTitle = "",
  organization = "",
  active = true,
}: Omit<ITestimonialCard, "__typename">) => {
  return (
    <VariantContainer
      variants={[
        css`
          transition: opacity 0.4s ease-in-out;
          opacity: ${active ? 1 : 0};
          height: ${active ? "auto" : 0};
          overflow: hidden;
        `,
      ]}
    >
      <VariantContainer
        variants={[
          css`
            display: flex;
            flex-direction: column;
          `,
          null,
          css`
            display: grid;
            grid-template-columns: ${image ? `1fr 1fr` : `fr`};
            grid-column-gap: 64px;
            padding: 48px;
          `,
        ]}
      >
        {image && (
          <VariantContainer
            variants={[
              css`
                img {
                  border-radius: 2px 2px 0 0;
                }
              `,
              null,
              css`
                img {
                  border-radius: 2px;
                }
              `,
            ]}
          >
            <ContentfulResponsiveImage
              {...image}
              style={{ height: "100%", width: "100%" }}
              imgStyle={{ objectFit: "cover" }}
            />
          </VariantContainer>
        )}
        <VariantContainer
          variants={[
            css`
              padding: 24px;
            `,
            css`
              padding: 32px;
            `,
            css`
              padding: 0;
            `,
          ]}
        >
          {logos && (
            <VariantContainer
              variants={[
                css`
                  display: flex;
                  justify-content: center;
                  & > * {
                    :not(:last-child) {
                      margin-right: 32px;
                    }
                  }
                  margin-bottom: 24px;
                `,
                null,
                css`
                  justify-content: ${image ? `flex-start` : `center`};
                  margin-bottom: 32px;
                `,
              ]}
            >
              {Array.isArray(logos) &&
                logos.map((logo, index) => (
                  <VariantContainer
                    variants={[
                      css`
                        width: 140px;
                      `,
                    ]}
                    key={index}
                  >
                    {logo?.fluid ? (
                      <Img
                        fluid={logo.fluid}
                        alt={logo.description || ""}
                        imgStyle={{ objectFit: "contain" }}
                      />
                    ) : (
                      <img src={logo.file.url} alt={logo.description || ""} />
                    )}
                  </VariantContainer>
                ))}
            </VariantContainer>
          )}
          <VariantContainer
            variants={[
              css`
                display: flex;
                flex-direction: column;
                text-align: center;
              `,
              null,
              css`
                text-align: ${image ? `left` : `center`};
              `,
            ]}
          >
            {typeof quote === "string" ? (
              <Text.Body
                variants={[
                  css`
                    ${T.sm.body(400)};
                    font-size: 16px;
                    color: var(--body-text-grey);
                    margin-bottom: 24px;
                  `,
                  css`
                    ${T.md.body(400)};
                    font-size: 16px;
                  `,
                  css`
                    ${T.lg.body(400)};
                    font-size: 16px;
                  `,
                ]}
              >
                {quote}
              </Text.Body>
            ) : (
              <ContentBlock.RichText
                variants={[
                  css`
                    margin-bottom: 24px;
                  `,
                ]}
              >
                {quote}
              </ContentBlock.RichText>
            )}
            <Text.Body
              variants={[
                css`
                  ${T.sm.body(500)};
                  font-size: 14px;
                  letter-spacing: 0.08em;
                  text-transform: uppercase;
                  color: var(--slate);
                `,
                css`
                  ${T.md.body(500)};
                  font-size: 14px;
                `,
                css`
                  ${T.lg.body(500)};
                  font-size: 16px;
                `,
              ]}
            >
              {author}
            </Text.Body>
            <Text.Body
              variants={[
                css`
                  ${T.sm.body(400)};
                  font-size: 14px;
                  letter-spacing: 0.08em;
                  text-transform: uppercase;
                  color: var(--body-text-grey);
                `,
                css`
                  ${T.md.body(400)};
                  font-size: 14px;
                `,
                css`
                  ${T.lg.body(400)};
                  font-size: 14px;
                `,
              ]}
            >
              {jobTitle}
              {organization && (
                <>
                  <br />
                  {organization}
                </>
              )}
            </Text.Body>
          </VariantContainer>
        </VariantContainer>
      </VariantContainer>
    </VariantContainer>
  )
}

export const TESTIMONIAL_TYPENAME = "ContentfulTestimonial"
export const TestimonialCardFragment = graphql`
  fragment ContentfulTestimonial on ContentfulTestimonial {
    __typename
    contentful_id
    quote {
      raw
    }
    author
    jobTitle
    organization
    logos {
      fluid(quality: 100) {
        ...GatsbyContentfulFluid_withWebp
      }
      file {
        url
      }
    }
    image {
      ... on ContentfulResponsiveImage {
        ...ContentfulResponsiveImage
      }
    }
  }
`
export default TestimonialCard
